import classNames from 'classnames';
import { useState } from 'react';
import { Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types';
import Typography from '@/components/atoms/typography/typography';
import MediaLightbox from '@/components/molecules/media-lightbox/media-lightbox';
import Splash from '@/components/molecules/splash/splash';
import { SearchBar } from '@/components/organisms/search-bar/search-bar';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import SecondaryHeaderItem from './secondary-header-item/secondary-header-item';
import SecondaryHeaderNavigation from './secondary-header-navigation/secondary-header-navigation';
import styles from './secondary-header.module.scss';

// !NOTE: Both SecondaryHeaderModuleDto and MediaHeaderWithSearchModuleDto are mapped to this component

export interface SecondaryHeaderProps extends ModuleWrapperProps {
  /** aspect ratio of media element */
  aspectRatio?: Content.Components['SecondaryHeaderModuleDto']['aspectRatio'];
  /** The splash in the first position */
  firstSplash: Content.Components['SecondaryHeaderModuleDto']['firstSplash'];
  /** The headline to show */
  headline: Content.Components['SecondaryHeaderModuleDto']['headline'];
  /** The tag style for the headline */
  headlineTagStyle?: Content.Components['SecondaryHeaderModuleDto']['headlineTagStyle'];
  /** The toggle for the search bar */
  hideSearch?: Content.Components['MediaHeaderWithSearchModuleDto']['hideSearch'];
  /** Indicates whether the headline wrapper should be shown below the image */
  isHeadlineBelowImage?: Content.Components['SecondaryHeaderModuleDto']['isHeadlineBelowImage'];
  /** The media item to show */
  mediaItems: Content.Components['SecondaryHeaderModuleDto']['mediaItems'];
  /** The pre headline to show */
  preHeadline: Content.Components['SecondaryHeaderModuleDto']['preHeadline'];
  /** The search settings for the search bar */
  searchSettings?: Content.Components['MediaHeaderWithSearchModuleDto']['searchSettings'];
  /** The splash in the second position */
  secondSplash: Content.Components['SecondaryHeaderModuleDto']['secondSplash'];
}

const SWIPER_CONFIGURATION: SwiperOptions = {
  slidesPerGroup: 1,
  slidesPerView: 1,
  spaceBetween: 0,
  speed: 500,
};

const SecondaryHeader = ({
  aspectRatio = 'twentyFiveByNine',
  firstSplash,
  headline,
  headlineTagStyle = 'Large',
  hideSearch = true,
  isHeadlineBelowImage = false,
  mediaItems = [],
  preHeadline,
  searchSettings,
  secondSplash,
  ...rest
}: SecondaryHeaderProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState<number>();

  const tagStyleMap = {
    Large: 'displayLarge',
    Medium: 'displayMedium',
  } as const;

  const propDefaultValues =
    searchSettings && searchSettings.$type === 'SearchSettingsDto'
      ? {
          adultsNumber:
            searchSettings.selectedPaxDistribution?.adultsNo.toString(),
          departureDate: searchSettings.selectedDepartureDate
            ? new Date(searchSettings.selectedDepartureDate.toString())
            : undefined,
          departureId: searchSettings.selectedDepartureId?.toString(),
          destinationIds: searchSettings.selectedDestinationIds?.map(id =>
            id.toString(),
          ),
          destinationRegionIds:
            searchSettings.selectedDestinationRegionIds?.map(id =>
              id.toString(),
            ),
          durationType: searchSettings.selectedPeriodType,
        }
      : undefined;

  const basicSearchSettings =
    searchSettings as Content.Components['SearchSettingsDto'];

  const travelPortalSearchSettings =
    searchSettings && searchSettings.$type === 'TravelPortalSearchSettingsDto'
      ? (searchSettings as Content.Components['TravelPortalSearchSettingsDto'])
      : undefined;

  return (
    <ModuleWrapper
      {...rest}
      className={classNames(styles.component, {
        [styles.hasHeadlineAsOverlay]: !isHeadlineBelowImage,
        [styles.hasSearchBar]: !hideSearch,
      })}
    >
      <Container className={styles.container}>
        <div className={styles.wrapper}>
          {firstSplash && (
            <Splash
              {...firstSplash}
              className={classNames(styles.splash, styles.isFirst)}
            />
          )}

          {secondSplash && (
            <Splash
              {...secondSplash}
              className={classNames(styles.splash, styles.isSecond)}
            />
          )}

          <Swiper
            {...SWIPER_CONFIGURATION}
            className={styles.swiper}
            loop={mediaItems.length > 1}
            modules={[Thumbs]}
            onSlideChangeTransitionStart={slide => {
              setCurrentIndex(slide.realIndex);
            }}
          >
            {mediaItems.map((item, itemIndex) => (
              <SwiperSlide key={itemIndex} className={styles.slide}>
                <SecondaryHeaderItem
                  key={itemIndex}
                  {...item}
                  className={classNames(styles.media, {
                    [styles.isSixteenByNine]: aspectRatio === 'sixteenByNine',
                  })}
                />
              </SwiperSlide>
            ))}

            {mediaItems.length > 1 && (
              <SecondaryHeaderNavigation
                currentIndex={currentIndex}
                onClickFullscreen={() => setActiveImageIndex(currentIndex)}
                pageCount={mediaItems.length}
              />
            )}
          </Swiper>

          <div className={styles.headlineWrapper}>
            <Typography className={styles.preHeadline} tagStyle="subHeading">
              {preHeadline}
            </Typography>

            <Typography
              className={styles.headline}
              tag="h1"
              tagStyle={tagStyleMap[headlineTagStyle]}
            >
              {headline}
            </Typography>
          </div>
        </div>

        {!hideSearch && searchSettings && (
          <div className={styles.searchBarWrapper}>
            <SearchBar
              compact
              propDefaultValues={propDefaultValues}
              searchSettings={basicSearchSettings}
              shouldNavigate
              travelPortalSearchSettings={travelPortalSearchSettings}
            />
          </div>
        )}
      </Container>

      <MediaLightbox
        isOpen={typeof activeImageIndex === 'number'}
        items={
          mediaItems.map(item => ({
            media: item,
          })) as any
        }
        onClose={() => setActiveImageIndex(undefined)}
        size="large"
        startIndex={activeImageIndex}
      />
    </ModuleWrapper>
  );
};

SecondaryHeader.displayName = 'SecondaryHeader';

export default SecondaryHeader;
